import React, { useState } from "react";
import { X, GripVertical } from "lucide-react";

// Drag-to-reorder photo grid built on native HTML5 DnD — no deps.
// `value` is an array of data: URLs or http URLs. `onChange(next)` fires with
// the reordered array. `onRemove(i)` and `onAdd(files)` are optional.
export const SortableGallery = ({
  value = [],
  onChange,
  onRemove,
  maxLabel,
  "data-testid": testId = "sortable-gallery",
}) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = value.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  if (!value || value.length === 0) return null;

  return (
    <div data-testid={testId}>
      {maxLabel && <p className="mb-2 text-xs text-slate-400">{maxLabel}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {value.map((src, i) => {
          const isCover = i === 0;
          const isDragging = dragIdx === i;
          const isOver = overIdx === i && dragIdx !== i;
          return (
            <div
              key={`${i}-${typeof src === "string" ? src.slice(-24) : i}`}
              draggable
              onDragStart={(e) => {
                setDragIdx(i);
                e.dataTransfer.effectAllowed = "move";
                try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* ignore */ }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overIdx !== i) setOverIdx(i);
              }}
              onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                move(dragIdx, i);
                setDragIdx(null);
                setOverIdx(null);
              }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`${testId}-item-${i}`}
              className={[
                "group relative aspect-[4/3] cursor-grab overflow-hidden rounded-xl border bg-slate-100 transition active:cursor-grabbing",
                isDragging ? "opacity-40" : "",
                isOver ? "border-brandblue ring-2 ring-brandblue/40" : "border-border",
              ].join(" ")}
            >
              <img src={src} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" draggable={false} />

              {/* Drag handle hint */}
              <div className="pointer-events-none absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition group-hover:opacity-100">
                <GripVertical size={14} />
              </div>

              {/* Index badge */}
              <span className="pointer-events-none absolute bottom-1 left-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">
                #{i + 1}{isCover ? " · cover" : ""}
              </span>

              {/* Order controls (keyboard-friendly fallback) */}
              <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition group-hover:opacity-100">
                {i > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); move(i, i - 1); }}
                    data-testid={`${testId}-up-${i}`}
                    className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-bold text-navy shadow"
                    title="Naar links"
                    aria-label="Verplaats naar links"
                  >←</button>
                )}
                {i < value.length - 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); move(i, i + 1); }}
                    data-testid={`${testId}-down-${i}`}
                    className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-bold text-navy shadow"
                    title="Naar rechts"
                    aria-label="Verplaats naar rechts"
                  >→</button>
                )}
                {onRemove && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                    data-testid={`${testId}-remove-${i}`}
                    className="rounded-full bg-white/90 p-1 text-danger shadow"
                    title="Verwijderen"
                    aria-label="Verwijder foto"
                  ><X size={12} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-slate-400">Sleep foto&apos;s om de volgorde aan te passen. Foto #1 is de coverfoto.</p>
    </div>
  );
};
